import React from "react";
import dayjs from "dayjs";
import { Badge, Box, Flex, Icon, Text, useColorModeValue } from "@chakra-ui/react";
import { MdVerified } from "react-icons/md";
import { getCertificationType, getStatusConfig } from "./certificationUtils";
import { EntityTypeSubtitle } from "./DetailHeaderParts";

/**
 * Colored pill badge with an icon, used for the type and status chips.
 * @param {Object} props
 * @param {React.ElementType} props.icon - react-icons component
 * @param {string} props.label - Badge label
 * @param {string} props.color - Accent color (hex)
 * @returns {React.ReactElement}
 */
function PillBadge({ icon, label, color }) {
    return (
        <Badge bg={`${color}20`} color={color} border={`1px solid ${color}40`}
            borderRadius="full" px="10px" py="3px" textTransform="none">
            <Flex align="center" gap="4px"><Icon as={icon} w="14px" h="14px" color={color} />{label}</Flex>
        </Badge>
    );
}

/**
 * Drawer header block: subject name, optional entity subtitle, type and status
 * badges, and the submission date.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @returns {React.ReactElement}
 */
function DrawerDetailHeader({ certification }) {
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    const dateColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const typeConfig = getCertificationType(certification);
    const statusConfig = getStatusConfig(certification.status);
    return (
        <Box>
            <Text fontSize="lg" fontWeight="700" color={nameColor} pr="24px">
                {certification.name || certification.artist_name || "Sans nom"}
            </Text>
            <EntityTypeSubtitle certification={certification} />
            <Flex align="center" gap="8px" mt="10px" wrap="wrap">
                <PillBadge icon={typeConfig.icon} label={typeConfig.label} color={typeConfig.color} />
                <PillBadge icon={MdVerified} label={statusConfig.label} color={statusConfig.color} />
            </Flex>
            {certification.created_at && (
                <Text fontSize="xs" color={dateColor} mt="8px">
                    Soumise le {dayjs(certification.created_at).format("DD/MM/YYYY HH:mm")}
                </Text>
            )}
        </Box>
    );
}

export default DrawerDetailHeader;
